// Yoj blocks: <yoj-refresh> keeps a server-rendered yoj block in step with the page. After a variant change
// (shopify:product:select) it takes its copy from the HTML the theme has already fetched; after a cart change
// (shopify:cart:lines-update) a cart-aware block re-renders its section. Updates are morphed in place where the
// theme provides its morph helper, so screen readers hear changed text such as the stock line.

const morphHelper = import('@theme/morph').then((module) => module.morph).catch(() => null);

async function update(element, fresh) {
  if (!fresh || !element.isConnected) return;
  // Attributes too, removals included: a panel hidden for a gift card shows again for a variant that's delivered
  for (const { name } of [...element.attributes]) if (!fresh.hasAttribute(name)) element.removeAttribute(name);
  for (const { name, value } of fresh.attributes) if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  const morph = await morphHelper;
  if (morph) morph(element, fresh);
  else element.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
  // The fresh copy carries server-side defaults (such as a countdown's starting "hidden"), so anything inside that
  // manages its own state redraws once the update is complete
  element.dispatchEvent(new Event('yoj:updated'));
}

function defineElement() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-refresh')) return;

  class YojRefresh extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section, dialog');
      this.onProductSelect = (event) => {
        // A product card's swatches belong to another product
        if (event.target instanceof Element && event.target.closest('product-card')) return;
        event.promise?.then((result) => this.adopt(result?.detail?.html)).catch(() => {});
      };
      this.section?.addEventListener('shopify:product:select', this.onProductSelect);
      if ('cartAware' in this.dataset) {
        this.onCartUpdate = (event) => { (event.promise ?? Promise.resolve()).then(() => this.reload()).catch(() => {}); };
        document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
      }
    }

    disconnectedCallback() {
      this.section?.removeEventListener('shopify:product:select', this.onProductSelect);
      if (this.onCartUpdate) document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    }

    adopt(html) {
      update(this, html?.querySelector(`yoj-refresh[data-key="${CSS.escape(this.dataset.key ?? '')}"]`));
    }

    // Re-render this block's section after a basket change. The cart renders it (a no-change cart update with
    // "sections"), so the basket read and the render happen together: a separate page request made straight after an
    // add can still see the old basket. The page is the current one, with the variant now picked in its product form
    // (Dwell doesn't always put the variant in the address).
    async reload() {
      const sectionId = this.section?.id?.replace(/^shopify-section-/, '');
      if (!sectionId) return;
      const page = new URL(location.href);
      const variant = this.section.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      if (variant) page.searchParams.set('variant', variant);
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: {}, sections: sectionId, sections_url: page.pathname + page.search }),
      });
      if (!response.ok) return;
      const html = (await response.json()).sections?.[sectionId];
      if (html) this.adopt(new DOMParser().parseFromString(html, 'text/html'));
    }
  }

  customElements.define('yoj-refresh', YojRefresh);
}

// <yoj-scroller>: a row of tiles that scrolls sideways (native swiping and trackpads), with arrow buttons that move
// it a screen at a time, disappear at either end, and hide altogether when everything already fits
function defineScroller() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-scroller')) return;

  class YojScroller extends HTMLElement {
    connectedCallback() {
      if (this.dataset.layout !== 'carousel') return;
      if (!this.track) {
        this.track = this.querySelector('[data-ref="track"]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-ref="prev"]');
        this.next = this.querySelector('[data-ref="next"]');
        this.arrows = this.prev?.parentElement;
        const move = (direction) => this.track.scrollBy({
          left: direction * this.track.clientWidth * 0.9,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        this.prev?.addEventListener('click', () => move(-1));
        this.next?.addEventListener('click', () => move(1));
        // A pixel or two of slack: zoomed pages scroll in fractions
        this.update = () => {
          const end = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= end;
          if (this.arrows) this.arrows.hidden = end <= 0;
        };
        this.track.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
      }
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }
  }

  customElements.define('yoj-scroller', YojScroller);
}

// <yoj-strip>: on phones the delivery strip shows one promise at a time, changing every few seconds. It holds still
// while touched or while a link in it has keyboard focus (that link's promise shows), while the tab is hidden, and
// while one of its promises is selected in the theme editor. The pause button stops it, and it starts paused for
// visitors who ask for reduced motion, who can press play. Wider screens show every promise and nothing moves.
function defineStrip() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-strip')) return;
  const phone = window.matchMedia('(max-width: 749px)');

  class YojStrip extends HTMLElement {
    connectedCallback() {
      if (!this.classList.contains('yoj-strip--phone-rotate')) return;
      if (!this.items) this.setUp();
      if (this.items.length < 2) return;
      document.addEventListener('visibilitychange', this.onChange);
      phone.addEventListener('change', this.onChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.update();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      document.removeEventListener('visibilitychange', this.onChange);
      phone.removeEventListener('change', this.onChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    // Once per element: its own listeners, which go with it
    setUp() {
      this.items = [...this.querySelectorAll('.yoj-strip__item:not(.yoj-strip__item--editor-note)')];
      this.pauseButton = this.querySelector('[data-ref="pause"]');
      this.index = Math.max(0, this.items.findIndex((item) => item.hasAttribute('data-current')));
      this.interval = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Focus inside holds it still, but pressing play means play, even though the button keeps the focus
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.focused = false;
        this.update();
      });
      this.addEventListener('pointerenter', () => { this.touching = true; this.update(); });
      this.addEventListener('pointerleave', () => { this.touching = false; this.update(); });
      this.addEventListener('focusin', (event) => {
        const item = event.target.closest('.yoj-strip__item');
        if (item) this.show(this.items.indexOf(item));
        this.focused = true;
        this.update();
      });
      this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.update(); } });
      this.onChange = () => this.update();
      this.onBlockSelect = (event) => {
        const i = this.items.findIndex((item) => item.dataset.blockId === event.detail?.blockId);
        if (i >= 0) { this.editing = true; this.show(i); this.update(); }
      };
      this.onBlockDeselect = () => { this.editing = false; this.update(); };
    }

    show(index) {
      if (index < 0) return;
      this.index = index % this.items.length;
      this.items.forEach((item, i) => item.toggleAttribute('data-current', i === this.index));
    }

    update() {
      const running = phone.matches && !this.userPaused && !this.touching && !this.focused && !this.editing && !document.hidden;
      if (running && !this.timer) this.timer = setInterval(() => this.show(this.index + 1), this.interval);
      if (!running && this.timer) { clearInterval(this.timer); this.timer = null; }
      if (this.pauseButton) {
        this.pauseButton.dataset.state = this.userPaused ? 'paused' : 'playing';
        this.pauseButton.setAttribute('aria-label', this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      }
    }
  }

  customElements.define('yoj-strip', YojStrip);
}

// <yoj-more>: text cut to a couple of lines (a collection's description), with a Read more button that appears only
// when there's more to read
function defineMore() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-more')) return;

  class YojMore extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-ref="text"]');
      this.toggle = this.querySelector('[data-ref="toggle"]');
      if (!this.text || !this.toggle || this.ready) return;
      this.ready = true;
      this.toggle.addEventListener('click', () => {
        const open = !this.hasAttribute('data-open');
        this.toggleAttribute('data-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.textContent = open ? this.toggle.dataset.less : this.toggle.dataset.more;
      });
      // Whether the text is cut short depends on its fonts and the width, so check again when either changes
      const check = () => { if (!this.hasAttribute('data-open')) this.toggle.hidden = this.text.scrollHeight <= this.text.clientHeight + 1; };
      new ResizeObserver(check).observe(this.text);
      document.fonts?.ready.then(check);
      check();
    }
  }

  customElements.define('yoj-more', YojMore);
}

defineElement();
defineScroller();
defineStrip();
defineMore();
